"use client";
import Link from 'next/link';
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
const DEFAULT_PLACEHOLDER_IMAGE = "";
import styles from "./dialog-section.module.css";
import { createUpdateHandler } from "./component-helpers";


const openDialog = (id) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lunar:open-dialog', { detail: { id } }));
  }
};
const PlayIcon = ({ style }) => <svg style={style} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>;

const showToast = (message, type = 'success', duration = 5000, link = null) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lunar:toast', {
      detail: { message, type, duration, link }
    }));
  }
};

const BuilderText = ({ tagName = 'p', content, className, style, children, id, sectionId, suffix, isVisible = true, tooltipIfTruncated, onUpdate, ...rest }) => {
  if (!isVisible) return null;
  const Tag = tagName;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  const effectiveSuffix = suffix || (className ? className.split(' ')[0] : tagName);
  let finalId = id || (normalizedSectionId ? normalizedSectionId + '-' + effectiveSuffix : undefined);
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const finalClassName = ("builder-text " + (className || '') + " " + (!content && !children ? 'empty-builder-text' : '')).trim();
  const title = tooltipIfTruncated ? content : undefined;
  const isSimpleString = content && typeof content === 'string' && !/<[a-z]|&[a-z0-9#]+;/i.test(content);
  return (
    <Tag
      id={finalId}
      className={finalClassName}
      style={style}
      dangerouslySetInnerHTML={content && !isSimpleString ? { __html: content } : undefined}
      title={title}
    >
      {isSimpleString ? content : (!content ? children : null)}
    </Tag>
  );
};

const BuilderImage = ({ src, mobileSrc, alt, className, style, mobileRatio, href, openInNewTab, linkType, targetDialogId, id, sectionId, suffix, isPortrait, isVisible = true, priority, aspectRatio, alwaysShowSrc, showStroke, showShadow, autoplay, enableAudio, onUpdate, watermarkSrc, ...rest }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const isAutoplay = autoplay === true || autoplay === "true" || autoplay === undefined;
  const [isAudioEnabled, setIsAudioEnabled] = useState(enableAudio === true || enableAudio === "true");
  const [isPlaying, setIsPlaying] = useState(isAutoplay);
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);

  const isVideoFile = (url) => url && typeof url === 'string' && url.match(/\.(mp4|webm|ogg|mov)$/i);
  const isYoutube = (url) => url && typeof url === 'string' && url.match(/^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.*$/);
  const isVimeo = (url) => url && typeof url === 'string' && url.match(/^(https?:\/\/)?(www\.)?(vimeo\.com)\/.*$/);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const needsImmediateLoad = !!(priority || alwaysShowSrc || !src || src === "" || (!typeof window === 'undefined' && !window.IntersectionObserver));
    if (needsImmediateLoad) { setShouldLoad(true); return; }
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setShouldLoad(true); observer.disconnect(); }
    }, { threshold: 0.01, rootMargin: '200px' });
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [src, priority, alwaysShowSrc, href, linkType]);

  useEffect(() => {
    if (isAutoplay && shouldLoad && videoRef.current) {
      videoRef.current.play().catch(() => {
        setIsPlaying(false);
      });
    }
  }, [isAutoplay, shouldLoad]);

  if (!isVisible) return null;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  let finalId = id || (normalizedSectionId && suffix ? normalizedSectionId + '-' + suffix : undefined);
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const effectiveAlt = alt || '';
  const isStrokeEnabled = showStroke === true || showStroke === "true";
  const isShadowEnabled = showShadow === true || showShadow === "true";
  let baseClassName = (className || '') + ' builder-image-container' + (isStrokeEnabled ? ' has-stroke' : '') + (isShadowEnabled ? ' has-shadow' : '');
  if (mobileRatio) baseClassName += " mobile-aspect-" + mobileRatio;

  const mediaStyle = { width: "100%", height: "100%", objectFit: ((className || '').includes('navigation') || (className || '').includes('logo') || (className || '').includes('object-contain')) ? 'contain' : 'cover', display: "block", ...style };
  const mediaClass = 'builder-image-media';

  const getYoutubeEmbedUrl = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const id = match && match[2].length === 11 ? match[2] : null;
    return id ? "https://www.youtube.com/embed/" + id + "?autoplay=" + (isAutoplay ? 1 : 0) + "&mute=" + (isAudioEnabled ? 0 : 1) + "&loop=1&playlist=" + id + "&controls=0" : url;
  };

  const getVimeoEmbedUrl = (url) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    const id = match ? match[1] : null;
    return id ? "https://player.vimeo.com/video/" + id + "?autoplay=" + (isAutoplay ? 1 : 0) + "&loop=1&muted=" + (isAudioEnabled ? 0 : 1) + "&background=" + (isAutoplay ? 1 : 0) : url;
  };

  let mediaContent;
  if (isYoutube(src)) {
    mediaContent = shouldLoad ? <iframe src={getYoutubeEmbedUrl(src)} className={mediaClass} style={{ ...mediaStyle, border: 'none' }} allow="autoplay; fullscreen" allowFullScreen /> : <div className={mediaClass} style={mediaStyle} />;
  } else if (isVimeo(src)) {
    mediaContent = shouldLoad ? <iframe src={getVimeoEmbedUrl(src)} className={mediaClass} style={{ ...mediaStyle, border: 'none' }} allow="autoplay; fullscreen" allowFullScreen /> : <div className={mediaClass} style={mediaStyle} />;
  } else if (isVideoFile(src)) {
    const videoSrc = (src && !src.startsWith('http') ? encodeURI(src) : src) + (!isAutoplay ? "#t=0.001" : "");
    mediaContent = (
      <>
        <video 
          key={src}
          ref={videoRef} 
          className={mediaClass} 
          style={mediaStyle} 
          autoPlay={isAutoplay && shouldLoad} 
          loop 
          muted={!isAudioEnabled} 
          playsInline 
          preload={!isAutoplay ? "auto" : (shouldLoad ? "metadata" : "none")} 
          onPlay={() => setIsPlaying(true)} 
          onPause={() => setIsPlaying(false)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (videoRef.current && !videoRef.current.paused) {
              videoRef.current.pause();
            }
          }}
        >
          {shouldLoad && (
            <>
              {mobileSrc && <source src={mobileSrc && !mobileSrc.startsWith('http') ? encodeURI(mobileSrc) : mobileSrc} media="(max-width: 767px)" />}
              {src && <source src={videoSrc} />}
            </>
          )}
          Your browser does not support the video tag.
        </video>
        {!isPlaying && (
          <button type="button" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10, border: 'none' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (videoRef.current) videoRef.current.play(); }}>
            <PlayIcon style={{ color: 'white', width: '32px', height: '32px' }} />
          </button>
        )}
      </>
    );
  } else {
    const imgContent = <img src={src || "https://space.lunaaar.site/assets-lunar/placeholder.svg"} alt={effectiveAlt} className={mediaClass} style={mediaStyle} loading={priority ? "eager" : "lazy"} crossOrigin={rest.crossOrigin} />;
    mediaContent = shouldLoad ? (
      mobileSrc ? (
        <picture style={{ display: 'contents' }}>
          <source media="(max-width: 767px)" srcSet={mobileSrc} />
          {imgContent}
        </picture>
      ) : imgContent
    ) : <div className={mediaClass} style={mediaStyle} />;
  }

  const wrapperStyle = { display: 'block', width: '100%', height: '100%', position: 'relative', overflow: 'hidden', ...style };

  const muteButton = (isYoutube(src) || isVimeo(src) || isVideoFile(src)) ? (
    <button
      type="button"
      className="media-mute-button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsAudioEnabled(!isAudioEnabled);
      }}
    >
      {isAudioEnabled ? (
        <svg className="media-mute-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.584 5.106a.75.75 0 011.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 11-1.06-1.06 8.25 8.25 0 000-11.668.75.75 0 010-1.06z" /><path d="M15.932 7.757a.75.75 0 011.061 0 6 6 0 010 8.486.75.75 0 01-1.06-1.061 4.5 4.5 0 000-6.364.75.75 0 010-1.06z" /></svg>
      ) : (
        <svg className="media-mute-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM17.78 9.22a.75.75 0 10-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 001.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 101.06-1.06L20.56 12l1.72-1.72a.75.75 0 00-1.06-1.06l-1.72 1.72-1.72-1.72z" /></svg>
      )}
    </button>
  ) : null;

  const watermarkContent = watermarkSrc ? (
    <img 
      src={watermarkSrc} 
      alt="Watermark" 
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 1.0,
        pointerEvents: 'none',
        zIndex: 2
      }} 
    />
  ) : null;

  const isNewTab = openInNewTab === true || openInNewTab === 'true';
  const targetAttr = isNewTab ? '_blank' : undefined;
  const relAttr = isNewTab ? 'noopener noreferrer' : 'noopener';
  const isMediaVideo = isVideoFile(src) || isYoutube(src) || isVimeo(src);

  if (!isMediaVideo && (href || (linkType === 'dialog' && targetDialogId))) {
    return <a ref={wrapperRef} id={finalId} href={href || '#'} target={targetAttr} rel={relAttr} className={baseClassName} style={wrapperStyle} onClick={(e) => { if (linkType === 'dialog') { e.preventDefault(); openDialog(targetDialogId); } }}>{mediaContent}{muteButton}{watermarkContent}</a>;
  }
  return <div ref={wrapperRef} className={baseClassName} style={wrapperStyle}>{mediaContent}{muteButton}{watermarkContent}</div>;
};
export default function DialogSection({
title,
description,
children,
isOpen: controlledIsOpen,
onUpdate,
onClose,
sectionId,
className = "",
image,
imageId,
imageVisible,
imageUrl,
imageLinkType,
imageTargetDialogId,
watermarkSrc,
onWatermarkSrcChange,
titleVisible,
descriptionVisible,
onTitleVisibleChange,
onDescriptionVisibleChange,
onImageVisibleChange,
imageEnableAudio,
onImageEnableAudioChange,
onImageAutoplayChange,
imageAutoplay = true,
imagePosition = "top",
isGenerating = false,
showWatermarkControl = true,
imageAspectRatioClass = "imagePlaceholder-16-9",
imageContainerClassName = "w-full",
imageContainerStyle = {},
showImageSrc = true,
readOnlyImage = false,
imageForceVideo = false,
imageCrossOrigin,
autoOpenOnLoad = false
}) {
const update = createUpdateHandler(onUpdate);
const [internalIsOpen, setInternalIsOpen] = useState(false);
const [portalContainer, setPortalContainer] = useState(null);
const [isClient, setIsClient] = useState(false);
const dialogRef = useRef(null);
const isControlled = controlledIsOpen !== undefined;
const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
useEffect(() => {
setPortalContainer(document.body);
setIsClient(true);
}, []);
useEffect(() => {
if (autoOpenOnLoad && isClient) {
toggleOpen(true);
}
}, [autoOpenOnLoad, isClient]);
const toggleOpen = useCallback((value) => {
if (isGenerating && value === false) return;
const newValue = value === undefined ? !isOpen : value;
if (newValue === false && isGenerating) return;
if (newValue === false && onClose) {
onClose();
}
if (isControlled) {
update('isOpen')(newValue);
} else {
setInternalIsOpen(newValue);
}
}, [isControlled, update, isOpen, onClose, isGenerating]);
useEffect(() => {
if (!isOpen) return;
const canvasScroll = document.getElementById('canvas-scroll-container');
const prevCanvasOverflow = canvasScroll ? canvasScroll.style.overflow : '';
const prevBodyOverflow = document.body.style.overflow;
if (canvasScroll) {
canvasScroll.style.overflow = 'hidden';
}
document.body.style.overflow = 'hidden';
return () => {
if (canvasScroll) {
canvasScroll.style.overflow = prevCanvasOverflow;
}
document.body.style.overflow = prevBodyOverflow;
};
}, [isOpen]);
useEffect(() => {
if (!isOpen) return;
const handleEsc = (e) => {
if (isGenerating) return;
if (e.key === 'Escape') toggleOpen(false);
};
window.addEventListener('keydown', handleEsc);
return () => window.removeEventListener('keydown', handleEsc);
}, [isOpen, toggleOpen, isGenerating]);
useEffect(() => {
if (!isOpen || !isGenerating) return;
if (typeof window === 'undefined') return;
const handleBeforeUnload = (e) => {
e.preventDefault();
e.returnValue = '';
return '';
};
window.history.pushState(null, '', window.location.href);
const handlePopState = () => {
window.history.pushState(null, '', window.location.href);
showToast("Please wait until AI generation is complete.", "warning");
};
window.addEventListener('beforeunload', handleBeforeUnload);
window.addEventListener('popstate', handlePopState);
return () => {
window.removeEventListener('beforeunload', handleBeforeUnload);
window.removeEventListener('popstate', handlePopState);
};
}, [isOpen, isGenerating]);
useEffect(() => {
const handleOpenDialog = (e) => {
if (e.detail?.id === sectionId) {
toggleOpen(true);
}
};
window.addEventListener('lunar:open-dialog', handleOpenDialog);
return () => window.removeEventListener('lunar:open-dialog', handleOpenDialog);
}, [sectionId, toggleOpen]);
useEffect(() => {
if (isOpen && dialogRef.current) {
const resetScroll = () => {
if (dialogRef.current) {
dialogRef.current.scrollTop = 0;
}
};
resetScroll();
const timer = setTimeout(resetScroll, 50);
const rafId = requestAnimationFrame(resetScroll);
return () => {
clearTimeout(timer);
cancelAnimationFrame(rafId);
};
}
}, [isOpen]);
if (!isClient) return null;
const hasTopImage = imagePosition === 'top' && imageVisible !== false;
return createPortal(
<div
className={`overlay z-system-modal-fullscreen ${className}`}
style={{
display: isOpen ? 'flex' : 'none',
alignItems: 'center',
justifyContent: 'center',
pointerEvents: 'auto',
overscrollBehavior: 'contain'
}}
onClick={() => !isGenerating && toggleOpen(false)}
data-section-id={sectionId}
data-dialog-overlay
>
<div className="container-grid" style={{ width: '100%' }}>
<div className="grid" style={{ width: '100%' }}>
<div className="col-mobile-4 col-tablet-6 col-desktop-6 offset-desktop-3 offset-tablet-1">
<div
ref={dialogRef}
className={`${styles.dialog} ${hasTopImage ? styles.hasTopImage : ''}`}
role="dialog"
aria-modal="true"
onClick={(e) => e.stopPropagation()}
style={{ margin: '0 auto' }}
>
{!isGenerating && (
<button className={`${styles.closeButton} z-content-2`} onClick={() => toggleOpen(false)} aria-label="Close dialog" data-dialog-close>
<X style={{ width: 20, height: 20 }} />
</button>
)}
{imagePosition === 'top' && (
<BuilderImage
className={`${styles.imageContainer} ${imageAspectRatioClass} object-cover`}
style={{ minHeight: '180px', ...imageContainerStyle }}
src={image || DEFAULT_PLACEHOLDER_IMAGE}
id={imageId}
sectionId={sectionId}
isVisible={imageVisible !== false}
suffix="image"
href={imageUrl}
linkType={imageLinkType}
targetDialogId={imageTargetDialogId}
showLinkControls={false}
alwaysShowSrc={true}
enableAudio={imageEnableAudio}
autoplay={imageAutoplay}
showAspectRatio={false}
showMobileRatio={false}
showAutoplayToggle={false}
watermarkSrc={watermarkSrc}
onWatermarkSrcChange={onWatermarkSrcChange || update('watermarkSrc')}
showWatermarkControl={showWatermarkControl}
showImageSrc={showImageSrc}
readOnly={readOnlyImage}
forceVideo={imageForceVideo}
crossOrigin={imageCrossOrigin}
/>
)}
<div className={styles.dialogBody}>
{((title && titleVisible !== false) || (description && descriptionVisible !== false) || imagePosition === 'bottom') && (
<div className={styles.textContainer}>
{title && (
<BuilderText
tagName="div"
className={`h4 ${styles.title}`}
content={title}
isVisible={titleVisible !== false}
onChange={update('title')}
placeholder="Dialog Title"
sectionId={sectionId}
suffix="title"
/>
)}
{description && (
<BuilderText
tagName="div"
className={`body-regular ${styles.description}`}
content={description}
isVisible={descriptionVisible !== false}
onChange={update('description')}
placeholder="Enter dialog description here..."
sectionId={sectionId}
suffix="description"
/>
)}
{imagePosition === 'bottom' && (
<div className={`mt-md ${imageContainerClassName}`} style={{ position: 'relative', margin: '0 auto', ...imageContainerStyle }}>
<BuilderImage
className={`${imageAspectRatioClass} object-cover w-full`}
style={{ borderRadius: 'var(--border-radius-lg)', display: 'block' }}
src={image || DEFAULT_PLACEHOLDER_IMAGE}
id={imageId}
sectionId={sectionId}
isVisible={imageVisible !== false}
suffix="image"
href={imageUrl}
linkType={imageLinkType}
targetDialogId={imageTargetDialogId}
showLinkControls={false}
alwaysShowSrc={true}
enableAudio={imageEnableAudio}
autoplay={imageAutoplay}
showAspectRatio={false}
showMobileRatio={false}
showAutoplayToggle={false}
watermarkSrc={watermarkSrc}
onWatermarkSrcChange={onWatermarkSrcChange || update('watermarkSrc')}
showWatermarkControl={showWatermarkControl}
showImageSrc={showImageSrc}
readOnly={readOnlyImage}
forceVideo={imageForceVideo}
crossOrigin={imageCrossOrigin}
/>
{isGenerating && (
<div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 'var(--border-radius-lg)', zIndex: 10, pointerEvents: 'none' }}>
<div style={{ border: '3px solid rgba(255,255,255,0.3)', borderTop: '3px solid #fff', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', marginBottom: '12px' }}></div>
<span className="caption-bold" style={{ margin: 'var(--padding-sm)', color: 'var(--content-neutral--body-invert)', textAlign: 'center' }}>Generating AI Content...</span>
</div>
)}
</div>
)}
</div>
)}
{children}
</div>
</div>
</div>
</div>
</div>
</div>,
portalContainer || (typeof document !== 'undefined' ? document.body : null)
);
}